/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { PortfolioItem, PricingTier } from '@shared/types';
import { portfolioItems as baseItems } from '@shared/data/portfolioData';
import { BACKEND_CONFIG, FIREBASE_CONFIG } from '../config';
import { firestoreConfigProvider } from './persistence/FirestoreConfigProvider';

const STORAGE_KEY = 'portfolio_config';

class PortfolioService {
  private items: PortfolioItem[] = [];
  private initialized = false;

  private async loadData() {
    if (this.initialized) return;
    
    let savedItems: PortfolioItem[] | null = null;
    
    if (FIREBASE_CONFIG.USE_FIREBASE_CONFIG_STORAGE) {
        try {
            const data = await firestoreConfigProvider.getPortfolioItems();
            if (data && data.length > 0) savedItems = data;
        } catch(e) {
            console.error('Failed to load portfolio from Firestore', e);
        }
    } else if (BACKEND_CONFIG.USE_EXTERNAL_BACKEND) {
        try {
            const res = await fetch(`${BACKEND_CONFIG.BACKEND_URL}/api/v1/portfolio`);
            const data = await res.json();
            if (data && data.length > 0) savedItems = data;
        } catch(e) {
            console.error('Failed to load portfolio from backend', e);
        }
    }
    
    if (!savedItems) {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                savedItems = parsed.items || parsed;
            } catch(e) {}
        }
    }
    
    if (savedItems && savedItems.length > 0) {
        // Merge saved config with base items
        this.items = baseItems.map(baseItem => {
          const savedItem = savedItems!.find((i: PortfolioItem) => i.id === baseItem.id);
          if (savedItem) {
            return {
              ...baseItem,
              isActive: savedItem.isActive !== undefined ? savedItem.isActive : baseItem.isActive,
              isOnlineService: savedItem.isOnlineService !== undefined ? savedItem.isOnlineService : baseItem.isOnlineService,
              pricingTiers: baseItem.pricingTiers.map((baseTier: PricingTier) => {
                const savedTier = savedItem.pricingTiers.find((t: PricingTier) => t.id === baseTier.id);
                if (savedTier) {
                  return {
                    ...baseTier,
                    basePriceEUR: savedTier.basePriceEUR !== undefined ? savedTier.basePriceEUR : baseTier.basePriceEUR
                  };
                }
                return baseTier;
              })
            };
          }
          return baseItem;
        });
    } else {
        this.items = [...baseItems];
    }
    this.initialized = true;
  }

  private async saveConfig(): Promise<void> {
    if (FIREBASE_CONFIG.USE_FIREBASE_CONFIG_STORAGE) {
        try {
            await firestoreConfigProvider.savePortfolioItems(this.items);
        } catch(e) {
            console.error('Failed to save portfolio to Firestore', e);
        }
    } else if (BACKEND_CONFIG.USE_EXTERNAL_BACKEND) {
        try {
            await fetch(`${BACKEND_CONFIG.BACKEND_URL}/api/v1/portfolio`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(this.items)
            });
        } catch(e) {
            console.error('Failed to save portfolio to backend', e);
        }
    } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ items: this.items }));
    }
  }

  public async getItems(): Promise<PortfolioItem[]> {
    await this.loadData();
    return this.items;
  }

  public async getActiveItems(): Promise<PortfolioItem[]> {
    await this.loadData();
    return this.items.filter(item => item.isActive !== false);
  }

  public async updateItemVisibility(id: string, isActive: boolean): Promise<void> {
    await this.loadData();
    const item = this.items.find(i => i.id === id);
    if (item) {
      item.isActive = isActive;
      await this.saveConfig();
    }
  }

  public async updateOnlineServiceStatus(id: string, isOnlineService: boolean): Promise<void> {
    await this.loadData();
    const item = this.items.find(i => i.id === id);
    if (item) {
      item.isOnlineService = isOnlineService;
      await this.saveConfig();
    }
  }

  public async updateTierPrice(itemId: string, tierId: string, newPrice: number): Promise<void> {
    await this.loadData();
    const item = this.items.find(i => i.id === itemId);
    if (item) {
      const tier = item.pricingTiers.find((t: PricingTier) => t.id === tierId);
      if (tier) {
        tier.basePriceEUR = newPrice;
        await this.saveConfig();
      }
    }
  }

  public async resetConfig(): Promise<void> {
    this.items = [...baseItems];
    localStorage.removeItem(STORAGE_KEY);
    await this.saveConfig();
  }
}

export const portfolioService = new PortfolioService();
